'use client';

import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@tanstack/react-query';
import { getChild, getChildAvailabilityById } from '@sola/proxy-sdk/children.ts';
import { MOCK_SUPPORTER } from '@sola/supporter-sdk/constants.ts';
import { MY_QUERY_KEYS } from '../constants.ts';
import { getMockChildDetails } from './mocks.ts';
type ChildDetailsData = NonNullable<Awaited<ReturnType<typeof getChild>>>;

/**
 * Hook to fetch and cache child details by child ID.
 */
export const useChildDetails = childId => {
  const $ = _c(7);
  let t0;
  let t1;
  if ($[0] !== childId) {
    t0 = [MY_QUERY_KEYS.childDetails, childId];
    t1 = async () => {
      if (!childId) {
        throw new Error("Missing child id.");
      }
      if (MOCK_SUPPORTER) {
        const childDetails = getMockChildDetails({
          childId,
          supporter: MOCK_SUPPORTER
        });
        if (!childDetails) {
          throw new Error(`Missing mock child details for child id "${childId}".`);
        }
        return childDetails;
      }
      const childDetails_0 = await getChild(childId);
      if (!childDetails_0) {
        throw new Error("Empty child details response.");
      }
      return childDetails_0;
    };
    $[0] = childId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  const t2 = !!childId;
  let t3;
  if ($[3] !== t0 || $[4] !== t1 || $[5] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2,
      refetchOnWindowFocus: false,
      staleTime: Infinity
    };
    $[3] = t0;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return useQuery(t3);
};

/**
 * Hook to fetch and cache child availability item by child ID.
 */
export const useChildAvailability = childId => {
  const $ = _c(7);
  let t0;
  let t1;
  if ($[0] !== childId) {
    t0 = [MY_QUERY_KEYS.childAvailability, childId];
    t1 = async () => {
      if (!childId) {
        throw new Error("Missing child id.");
      }
      const availability = await getChildAvailabilityById({
        globalId: childId
      });
      if (!availability) {
        throw new Error("Empty child availability response.");
      }
      return availability;
    };
    $[0] = childId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  const t2 = !!childId;
  let t3;
  if ($[3] !== t0 || $[4] !== t1 || $[5] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2,
      refetchOnWindowFocus: false,
      staleTime: Infinity
    };
    $[3] = t0;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return useQuery(t3);
};